import { EncodingOperation, EncoderToolType } from '../types/EncodingOperation';

export type ImageFormat = 'image/png' | 'image/jpeg' | 'image/webp';

export interface ConversionOptions {
  quality?: number; // 0-100, for JPEG and WEBP
}

interface ImageFormatInfo {
  name: string;
  extension: string;
  supportsTransparency: boolean;
  supportsQuality: boolean;
  defaultQuality: number;
}

export class ImageConverterService {
  private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  private static readonly SUPPORTED_INPUT_FORMATS = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  private static readonly SUPPORTED_OUTPUT_FORMATS: ImageFormat[] = ['image/png', 'image/jpeg', 'image/webp'];

  private static readonly FORMAT_INFO: Record<ImageFormat, ImageFormatInfo> = {
    'image/png': {
      name: 'PNG',
      extension: 'png',
      supportsTransparency: true,
      supportsQuality: false,
      defaultQuality: 100
    },
    'image/jpeg': {
      name: 'JPEG',
      extension: 'jpg',
      supportsTransparency: false,
      supportsQuality: true,
      defaultQuality: 90
    },
    'image/webp': {
      name: 'WebP',
      extension: 'webp',
      supportsTransparency: true,
      supportsQuality: true,
      defaultQuality: 85
    }
  };

  /**
   * Convert image to a different format
   */
  static async convert(
    imageFile: File,
    targetFormat: ImageFormat,
    options: ConversionOptions = {}
  ): Promise<EncodingOperation> {
    const operationId = this.generateOperationId();

    try {
      // Validation
      this.validateFile(imageFile);
      this.validateFormat(targetFormat);

      // Get quality setting
      const formatInfo = this.FORMAT_INFO[targetFormat];
      const quality = options.quality !== undefined
        ? options.quality / 100
        : formatInfo.defaultQuality / 100;

      // Load image
      const imageData = await this.loadImage(imageFile);

      // Convert to target format
      const outputBlob = await this.imageDataToBlob(imageData, targetFormat, quality);

      return {
        id: operationId,
        toolType: EncoderToolType.IMAGE_CONVERTER,
        operationType: 'encode',
        inputData: imageFile,
        outputData: outputBlob as File,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          sourceFormat: imageFile.type,
          targetFormat,
          sourceSize: imageFile.size,
          targetSize: outputBlob.size,
          quality: Math.round(quality * 100),
          compressionRatio: ((1 - outputBlob.size / imageFile.size) * 100).toFixed(2) + '%'
        }
      };
    } catch (error) {
      return {
        id: operationId,
        toolType: EncoderToolType.IMAGE_CONVERTER,
        operationType: 'encode',
        inputData: imageFile,
        outputData: null,
        timestamp: new Date(),
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get format information
   */
  static getFormatInfo(format: ImageFormat): ImageFormatInfo {
    return this.FORMAT_INFO[format];
  }

  /**
   * Get all supported output formats
   */
  static getSupportedFormats(): ImageFormat[] {
    return this.SUPPORTED_OUTPUT_FORMATS;
  }

  /**
   * Check if format supports transparency
   */
  static supportsTransparency(format: ImageFormat): boolean {
    return this.FORMAT_INFO[format].supportsTransparency;
  }

  /**
   * Check if format supports quality adjustment
   */
  static supportsQuality(format: ImageFormat): boolean {
    return this.FORMAT_INFO[format].supportsQuality;
  }

  /**
   * Get default quality for format
   */
  static getDefaultQuality(format: ImageFormat): number {
    return this.FORMAT_INFO[format].defaultQuality;
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return bytes + ' B';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }

  /**
   * Validate uploaded file
   */
  private static validateFile(file: File): void {
    if (!file) {
      throw new Error('No file provided');
    }

    if (!this.SUPPORTED_INPUT_FORMATS.includes(file.type)) {
      throw new Error(`Unsupported input format. Supported: ${this.SUPPORTED_INPUT_FORMATS.join(', ')}`);
    }

    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(`File size exceeds ${this.MAX_FILE_SIZE / 1024 / 1024}MB limit`);
    }
  }

  /**
   * Validate target format
   */
  private static validateFormat(format: ImageFormat): void {
    if (!this.SUPPORTED_OUTPUT_FORMATS.includes(format)) {
      throw new Error(`Unsupported output format: ${format}`);
    }
  }

  /**
   * Load image file to ImageData
   */
  private static async loadImage(file: File): Promise<ImageData> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });

          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }

          // For JPEG conversion, fill with white background
          if (file.type === 'image/png' || file.type === 'image/webp') {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }

          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          resolve(imageData);
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  /**
   * Convert ImageData to Blob in target format
   */
  private static async imageDataToBlob(
    imageData: ImageData,
    format: ImageFormat,
    quality: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // For JPEG, fill with white background for transparency
      if (format === 'image/jpeg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.putImageData(imageData, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from image data'));
          }
        },
        format,
        quality
      );
    });
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `img-convert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
